import { gql } from "@apollo/client";
import { Box, Button, Icon, Layout, Link, Modal, Page, Text, TextField } from "@shopify/polaris";
import { ArrowLeftMinor, HideMinor, PasskeyMinor, PersonalizedTextMajor, ViewMinor } from "@shopify/polaris-icons";
import React, { useCallback, useState } from "react";
import { useHistory } from "react-router";
import { useToken } from "src/components/authentication/TokenProvider";
import { PolarisCard } from "src/components/shopify/PolarisCard";
import { Graph } from "src/generated/graph";
import {
  useForgetSupplierPasswordMutation,
  useResendSupplierOtpMutation,
  useVerifySupplierOtpMutation,
} from "src/graphql/codegen_graphql";
import { Toast } from "src/hooks/toast";
import useWindowDimensions from "src/hooks/useWindow";
import { CreateApolloClient } from "src/lib/ApolloClient";

const MUTATION_RESET = gql`
  mutation resetSupplierPassword($newPassword: String!) {
    resetSupplierPassword(newPassword: $newPassword)
  }
`;

function ModalVerifyOtp(props: {
  passKey: string;
  isSeller: boolean;
  open: boolean;
  onClose: any;
  onChangeKey: (v: string) => void;
}) {
  const q = new URLSearchParams(window.location.search);
  const [otpInput, setOtpInput] = useState(["", "", "", ""]);
  const [focus, setFocus] = useState(0);
  const { setToken } = useToken();
  const [verifyOtp, { loading: loading1 }] = useVerifySupplierOtpMutation();
  const [resendOtp, { loading: loading2 }] = useResendSupplierOtpMutation();

  const handleChangeInput = useCallback(
    (value, index) => {
      const v = [...otpInput];
      v[index] = value;
      setOtpInput(v);
      value !== "" && index < v.length - 1 && setFocus(index + 1);
      value === "" && index > 0 && setFocus(index - 1);
    },
    [otpInput],
  );

  const handleVerify = useCallback(() => {
    verifyOtp({
      variables: {
        verifyToken: props.passKey,
        secretCode: otpInput.join(""),
        isSeller: true,
        verifyForgeted: true,
      },
    }).then((res) => {
      if (!!res.data?.verifySupplierOtp?.success) {
        Toast.onShow(res.data?.verifySupplierOtp?.message || "", "success", "right");
        if (res.data.verifySupplierOtp.status === "FORGET") {
          // sessionStorage.setItem('forget_token', res.data.verifySupplierOtp.token + '')
          setToken(res.data.verifySupplierOtp.token + "");
          props.onClose();
        }
      } else {
        Toast.onShow(res.data?.verifySupplierOtp?.message || "", "danger", "right");
      }
    });
  }, [otpInput, props, verifyOtp, setToken]);

  const handleResendOtp = useCallback(() => {
    resendOtp({
      variables: {
        verifyToken: props.passKey,
        verifyForgeted: true,
      },
    }).then((res) => {
      if (res.data?.resendSupplierOtp?.success) {
        Toast.onShow(res.data.resendSupplierOtp.message || "", "success", "right");
        props.onChangeKey(res.data.resendSupplierOtp.token || "");
        q.set("passkey", res.data.resendSupplierOtp.token || "");
      }
    });
  }, [q, props, resendOtp]);

  return (
    <Modal title="" titleHidden open={props.open} onClose={props.onClose}>
      <Modal.Section>
        <Box padding={"300"}>
          <Text as="h3" variant="headingXl" alignment="center">
            Enter the code
          </Text>
        </Box>
        <Box padding={"300"}>
          <Text as="p" variant="bodyMd" alignment="center">
            We sent a code to your phone number or email to confirm.
          </Text>
        </Box>
        <Box padding={"300"}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              style={{ display: "flex", justifyContent: "space-between", width: 300 }}
              onKeyDown={(e) => e.keyCode === 13 && handleVerify()}
            >
              {[...new Array(4)].map((x, i) => {
                return (
                  <div key={i} style={{ width: 40 }}>
                    <TextField
                      focused={focus === i}
                      onChange={(v) => handleChangeInput(v, i)}
                      label=""
                      align="center"
                      labelHidden
                      value={otpInput[i]}
                      autoComplete="off"
                      maxLength={1}
                      minLength={1}
                      loading={loading1 || loading2}
                      disabled={loading1 || loading2}
                      selectTextOnFocus
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </Box>
        <br />
        <Box padding={"300"}>
          <Button
            fullWidth
            tone="success"
            variant="primary"
            loading={loading1 || loading2}
            disabled={loading1 || loading2}
            onClick={handleVerify}
          >
            Verify Account
          </Button>
        </Box>
        <Box padding={"300"}>
          <Text as="p" variant="bodyMd" alignment="center">
            If you not receive code or code was expire. <Link onClick={handleResendOtp}>Resend Code</Link>
          </Text>
        </Box>
      </Modal.Section>
    </Modal>
  );
}

export function ForgetScreen() {
  const { width } = useWindowDimensions();
  const { token } = useToken();
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [forgetInput, setForgetInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status] = useState<"FORGET" | "VERIFY" | "VERIFIED">("FORGET");
  const [info, setInfo] = useState({
    code: "",
    passkey: "",
    confirmPassword: "",
    password: "",
  });
  const [forget, { loading }] = useForgetSupplierPasswordMutation();

  const handleSubmit = useCallback(() => {
    if (!token && forgetInput) {
      forget({
        variables: {
          username: forgetInput,
        },
      }).then((res) => {
        if (res.data?.forgetSupplierPassword) {
          Toast.onShow(res.data.forgetSupplierPassword.message || "", "success", "right");
          setInfo({
            ...info,
            passkey: res.data.forgetSupplierPassword.token || "",
          });
          setTimeout(() => {
            setOpen(true);
          }, 500);
        } else {
          Toast.onShow(res.data?.forgetSupplierPassword?.message || "", "danger", "right");
        }
      });
      return;
    }

    if (info.password !== info.confirmPassword) {
      Toast.onShow("Password not matched!", "danger", "right");
      return;
    }

    CreateApolloClient("mx" + token)
      .mutate<Graph.Mutation>({
        mutation: MUTATION_RESET,
        variables: {
          newPassword: info.password,
        },
      })
      .then((res) => {
        if (res.data?.resetSupplierPassword) {
          Toast.onShow("Your request change password was successful", "success", "right");
          history.push("/");
          setTimeout(() => {
            localStorage.removeItem("token");
            window.location.reload();
          }, 500);
        } else {
          Toast.onShow("Something was wrong please try again!", "danger", "right");
        }
      })
      .catch(() => {
        Toast.onShow("Something was wrong please try again!", "danger", "right");
      });
  }, [forgetInput, info, forget, history, token]);

  return (
    <Page title="Forget Password" titleHidden>
      <Layout>
        <Layout.Section variant="oneThird">
          <div style={{ position: "relative" }}>
            <div
              style={{
                width: width > 600 ? 600 : width,
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%,50%)",
              }}
            >
              <PolarisCard>
                <Box padding={"0"}>
                  <Text as="h3" variant="headingXl" alignment="center">
                    Forget Password
                  </Text>
                </Box>
                <br />
                {!token && status === "FORGET" && (
                  <Box padding={"300"}>
                    <div onKeyDown={(e) => e.keyCode === 13 && handleSubmit()}>
                      <TextField
                        autoComplete="off"
                        label="Email or Phone number"
                        prefix={<Icon source={PersonalizedTextMajor} />}
                        placeholder="Enter email or phone number...."
                        value={forgetInput}
                        onChange={setForgetInput}
                        requiredIndicator
                        type="text"
                        disabled={loading}
                      />
                    </div>
                  </Box>
                )}
                {/* {
                  status === 'VERIFY' && <OtpComponent />
                } */}
                <ModalVerifyOtp
                  passKey={info.passkey}
                  isSeller={true}
                  open={open}
                  onClose={() => {
                    setOpen(false);
                  }}
                  onChangeKey={(key) => setInfo({ ...info, passkey: key })}
                />
                {token && (
                  <Box padding={"300"}>
                    <div onKeyDown={(e) => e.keyCode === 13 && handleSubmit()}>
                      <TextField
                        autoComplete="off"
                        label="New Password"
                        placeholder="Enter new password..."
                        type={showPassword ? "text" : "password"}
                        value={info.password}
                        requiredIndicator
                        onChange={(v) => {
                          setInfo({
                            ...info,
                            password: v,
                          });
                        }}
                        prefix={<Icon source={PasskeyMinor as any} />}
                        suffix={
                          <div className="cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                            <Icon source={showPassword ? (HideMinor as any) : (ViewMinor as any)} />
                          </div>
                        }
                        // error={!!error}
                        // helpText={error?.message}
                      />
                      <br />
                      <TextField
                        autoComplete="off"
                        label="Confirm Password"
                        placeholder="Enter confirm password..."
                        type={showPassword ? "text" : "password"}
                        value={info.confirmPassword}
                        requiredIndicator
                        onChange={(v) => {
                          setInfo({
                            ...info,
                            confirmPassword: v,
                          });
                        }}
                        prefix={<Icon source={PasskeyMinor as any} />}
                        suffix={
                          <div className="cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                            <Icon source={showPassword ? (HideMinor as any) : (ViewMinor as any)} />
                          </div>
                        }
                        // error={!!error}
                        // helpText={error?.message}
                      />
                    </div>
                  </Box>
                )}
                <Box padding={"300"}>
                  <Button
                    fullWidth
                    tone="success"
                    variant="primary"
                    // loading={loading1 || loading2}
                    // disabled={loading1 || loading2}
                    onClick={handleSubmit}
                  >
                    {!token ? "Send Code" : "Change Password"}
                  </Button>
                  <br />
                  <br />
                  <Button fullWidth url="/" variant="plain" icon={ArrowLeftMinor} loading={loading} disabled={loading}>
                    Back to log in
                  </Button>
                </Box>
              </PolarisCard>
            </div>
          </div>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
